/**
 * AuthContext - Unified authentication context for CMS
 * Supports both Upercut and Pro user authentication
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Roles } from '@utils/roleUtils';

export interface User {
  userId: string;
  email: string;
  role: Roles;
  fullName?: string;
  photo?: string;
  status: boolean;
  active: boolean;
  // Additional user fields from LoginData
  adminProId?: string;
  logo?: string;
  professions?: string[];
  specialties?: string[];
  bossFirstname?: string;
  bossLastname?: string;
  name?: string;
  commercialName?: string;
  onboardingCompleteDate?: Date;
  acceptTnc?: boolean;
}

interface AuthContextValue {
  // Authentication state
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Role checks
  isUpercut: boolean;
  isPro: boolean;
  isTalent: boolean;
  
  // Actions
  login: (loginData: any) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        // Clear invalid data
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }
    
    setIsLoading(false);
  }, []);

  // Login action - store token and user data
  const login = useCallback((loginData: any) => {
    const { token: newToken, ...userData } = loginData;
    
    if (!newToken || !userData.role) {
      console.error('Invalid login data: missing token or role');
      return;
    }

    // Store in localStorage
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('authUser', JSON.stringify(userData));

    // Update state
    setToken(newToken);
    setUser(userData as User);
  }, []);

  // Logout action - clear all auth data
  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('selectedProId'); // Clear proId selection for Upercut users

    // Clear state
    setToken(null);
    setUser(null);
  }, []);

  // Update user data
  const updateUser = useCallback((userData: Partial<User>) => {
    if (!user) return;

    const updatedUser = { ...user, ...userData };
    
    // Update localStorage
    localStorage.setItem('authUser', JSON.stringify(updatedUser));
    
    // Update state
    setUser(updatedUser);
  }, [user]);

  // Role checks
  const isUpercut = user?.role === Roles.UPERCUT;
  const isPro = [Roles.PRO, Roles.COMPANY, Roles.FREELANCE].includes(user?.role as Roles);
  const isTalent = user?.role === Roles.TALENT;

  const value: AuthContextValue = {
    token,
    user,
    isAuthenticated: !!token && !!user,
    isLoading,
    isUpercut,
    isPro,
    isTalent,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 * Must be used within AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
